import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { tv } from 'tailwind-variants'
import { IContextMenuState } from 'types'
import { DEFAULT_MENU_WIDTH } from 'style/constants'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { KEY_PRESS } from 'components/spreadsheet/constants/const'
import { HOVER_RESET_EVENT } from 'hooks/useHovered'
import { MenuClose, MenuCloseContext } from './context'

// The menu's frame, in the manner of shadcn/ui: the raised surface, a hairline border and a soft
// shadow, dropping in with a quick fade (a slide, not a scale: the menu measures its own height to
// stay on screen, and a scale would shrink it mid-measure). Rows go in a <MenuList> of <MenuItem>s
// from ./items.
const menu = tv({
  base: 'bg-surface-raised rounded-lg border border-divider select-none h-fit whitespace-nowrap text-sm overflow-auto',
  variants: {
    // Sized to its rows (see `minWidth`), or as wide as `width`.
    fit: {
      true: 'w-max',
      false: 'w-full'
    },
    pinned: {
      true: 'fixed overscroll-contain',
      false: 'absolute'
    },
    fullScreen: {
      // A full-screen sheet has nothing to float over, so no shadow and no drop-in.
      true: 'fixed inset-0 w-full h-full rounded-none border-0 overscroll-contain',
      false: 'shadow-[0_4px_12px_-2px_rgb(0_0_0/0.12),0_2px_4px_-2px_rgb(0_0_0/0.08)]'
    },
    // The drop-in moves away from the trigger: down from one above, up from one below.
    drop: {
      down: 'animate-menu-in motion-reduce:animate-none',
      up: 'animate-menu-in-up motion-reduce:animate-none',
      none: ''
    }
  }
})

// `max` gives way to `min` when the range is empty, e.g. a menu wider than the viewport.
export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), Math.max(min, max))

/** The gap a menu keeps from the bottom of the screen. */
export const menuBottomGap = (viewportMargin: number): number => Math.max(10, viewportMargin)

export interface MenuProps {
  id: string
  menuState: IContextMenuState
  setMenuState: (menuState: IContextMenuState) => void
  width?: number
  /**
   * Sizes the menu to its rows: at least this wide, and at most `width`, beyond which rows truncate. It's
   * measured as the menu opens and then kept, so searching or pointing at its rows doesn't resize it.
   */
  minWidth?: number
  children?: React.ReactNode
  zIndex?: number
  onClose?: () => void
  rootComponent?: HTMLElement | null
  inline?: boolean
  shouldBlockClose?: boolean
  confirmMessage?: string
  viewportMargin?: number
  fixed?: boolean
  nested?: boolean
  fullScreen?: boolean
  /** False keeps the menu open on a click outside it; Esc and its own controls still close it. */
  closeOnClickOutside?: boolean
  /** How far the menu has been moved from where it opened (see FloatingMenu). */
  offset?: { x: number; y: number }
}

/**
 * Where menus render when not told otherwise: the app's root, or, inside a dialog, a layer of the dialog's
 * own. A modal dialog keeps focus in itself and closes on a press outside it, so a menu opened from it has
 * to be inside it.
 */
export const MenuLayer = createContext<HTMLElement | null>(null)

// The menus open now, in the order they opened.
const openMenus: symbol[] = []

// Drops the click that follows a press on `element`, before React hears of it. Only that click: once the mouse
// button or key is released, whatever click there was has been dispatched, and the listener goes.
const dropNextClickOn = (element: HTMLElement, release: 'mouseup' | 'keyup' = 'mouseup'): void => {
  const drop = (event: MouseEvent): void => {
    if (!element.contains(event.target as Node)) return
    event.stopPropagation()
    event.preventDefault()
    document.removeEventListener('click', drop, { capture: true })
  }
  document.addEventListener('click', drop, { capture: true })
  window.addEventListener(
    release,
    () => setTimeout(() => document.removeEventListener('click', drop, { capture: true }), 0),
    { capture: true, once: true }
  )
}

// The element last pressed (by mouse, or with Enter or Space), so a menu can tell which control opened it.
let lastPressed: Element | null = null
if (typeof document !== 'undefined') {
  const press = (event: Event): void => {
    lastPressed = event.target instanceof Element ? event.target : null
  }
  document.addEventListener('mousedown', press, { capture: true })
  document.addEventListener('click', press, { capture: true })
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') press(event)
    },
    { capture: true }
  )
}

// The control that opened a menu, from what was pressed as it opened: the nearest element that says whether it's
// expanded.
const openerOf = (pressed: Element | null): HTMLElement | null =>
  (pressed?.closest?.('[aria-expanded]') as HTMLElement | null) ?? null

// In a press's path, the control showing this menu as open: its opener, or a control around or inside it (a row
// around a swatch). Any other expanded control (an open folder in the sidebar) keeps its press.
const pressedOpener = (path: EventTarget[], opener: HTMLElement | null): HTMLElement | undefined =>
  opener
    ? (path.find(
        (element) =>
          element instanceof HTMLElement &&
          element.getAttribute('aria-expanded') === 'true' &&
          (element === opener || element.contains(opener) || opener.contains(element))
      ) as HTMLElement | undefined)
    : undefined

const Menu: React.FC<MenuProps> = ({
  id,
  menuState,
  setMenuState,
  children,
  width,
  minWidth,
  zIndex,
  onClose,
  rootComponent,
  inline = false,
  shouldBlockClose = false,
  confirmMessage = 'There are unsaved changes - are you sure you wish to continue?',
  viewportMargin = 0,
  fixed = false,
  fullScreen = false,
  nested = false,
  closeOnClickOutside = true,
  offset
}) => {
  const layer = useContext(MenuLayer)
  const appRootElement = layer || document.getElementById('root') || document.body // Default to body if root is not found
  const contextMenuRef = useRef<HTMLDivElement>(null)
  const componentWidth = width || DEFAULT_MENU_WIDTH
  const [componentHeight, setComponentHeight] = useState<number>(0)
  const [viewport, setViewport] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }))

  const offsetX = fullScreen ? 0 : (offset?.x ?? 0)
  const offsetY = fullScreen ? 0 : (offset?.y ?? 0)
  // A menu sized to its rows, as it measured at this opening (an opening is where it opened from).
  const fit = !!minWidth && !inline && !fullScreen
  const anchor = menuState.open ? `${menuState.top}|${menuState.left}|${menuState.bottom}` : null
  const [fitted, setFitted] = useState<{ anchor: string | null; width: number } | null>(null)
  // Closing forgets it, so reopening from the same trigger measures the rows afresh.
  if (!anchor && fitted) setFitted(null)
  const fittedWidth = fit && fitted?.anchor === anchor ? fitted.width : undefined

  // Mirror the close-guard props into refs so the document listeners (below) always read the
  // latest values regardless of when they were registered.
  const shouldBlockCloseRef = useRef(shouldBlockClose)
  const confirmMessageRef = useRef(confirmMessage)
  useLayoutEffect(() => {
    shouldBlockCloseRef.current = shouldBlockClose
    confirmMessageRef.current = confirmMessage
  }, [shouldBlockClose, confirmMessage])

  // The control that opened it, if a press opened it, so a press on that control again only closes it.
  const openerRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    openerRef.current = menuState.open ? openerOf(lastPressed) : null
  }, [menuState.open])

  // Its place among the open menus, newest last, while it's open: Escape closes only the newest.
  const [openToken] = useState(() => Symbol('menu'))
  useEffect(() => {
    if (!menuState.open) return
    openMenus.push(openToken)
    return () => {
      const index = openMenus.indexOf(openToken)
      if (index !== -1) openMenus.splice(index, 1)
    }
  }, [menuState.open, openToken])

  const handleMenuClose: MenuClose = ({ force = false, discard = false } = {}) => {
    if (!force && shouldBlockCloseRef.current && !window.confirm(confirmMessageRef.current)) return
    if (onClose && !discard) onClose()
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (!closeOnClickOutside || !menuState.open || !contextMenuRef.current) return

    const composedPath = event.composedPath()
    const menuElement = contextMenuRef.current
    // A click in a nested menu opened from this one (so later in the page: menus go into it as they
    // open) is still a click in this one. A click in one opened before it, such as the popover this
    // one was opened from, is a click outside it.
    const openedFromThis = (element: HTMLElement) =>
      !!(menuElement.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING)
    const isInside = composedPath.some((element) => {
      const htmlElement = element as HTMLElement
      return (
        htmlElement === menuElement ||
        (htmlElement.dataset?.nestedMenu === 'true' && openedFromThis(htmlElement)) ||
        (htmlElement.id &&
          (htmlElement.id.includes('select-options') ||
            htmlElement.id.includes('change-column-kind-modal') ||
            htmlElement.id.includes('sort-menu-options')))
      )
    })

    if (!isInside) {
      // Pressed on the control showing it as open, the press only closes it: the click that follows would
      // otherwise reach the control and open it straight back.
      const opener = pressedOpener(composedPath, openerRef.current)
      if (opener) dropNextClickOn(opener)
      handleMenuClose()
    }
  }

  // Escape closes the innermost menu first: while a nested menu (or dialog) opened from this one is open,
  // it's that one's to close.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (!menuState.open) return
    // Enter or Space on the control showing it as open closes it too, as a press on it does: the click those
    // keys make is dropped. Only the newest menu answers, and only to a control outside it.
    if ((event.key === 'Enter' || event.key === ' ') && openMenus[openMenus.length - 1] === openToken) {
      const opener = pressedOpener(event.composedPath(), openerRef.current)
      if (opener && contextMenuRef.current && !contextMenuRef.current.contains(opener)) {
        dropNextClickOn(opener, 'keyup')
        handleMenuClose()
      }
      return
    }
    if (event.key !== KEY_PRESS.esc) return
    // A menu opened after this one (a list picked from in it, say) is still open: it's that one's to close.
    if (openMenus[openMenus.length - 1] !== openToken) return
    const menuElement = contextMenuRef.current
    const nestedOpen =
      !!menuElement &&
      // A submenu beside a menu's row (MenuSub) isn't a menu of its own and doesn't answer Escape: the menu does.
      Array.from(document.querySelectorAll<HTMLElement>('[data-nested-menu="true"]:not([data-submenu])')).some(
        (element) =>
          element !== menuElement && !!(menuElement.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING)
      )
    if (!nestedOpen) handleMenuClose()
  }
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  })

  // A menu that closes under a stationary pointer leaves whatever it covered without any
  // boundary event, so pointer-tracked hover state would keep whatever it held while the menu
  // was open. Release it as the menu goes away.
  useEffect(() => {
    if (!menuState.open) return
    return () => {
      document.dispatchEvent(new Event(HOVER_RESET_EVENT))
    }
  }, [menuState.open])

  useLayoutEffect(() => {
    const element = contextMenuRef.current
    if (!menuState.open || !element) return

    const measure = () => setComponentHeight(element.getBoundingClientRect().height)
    const onResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight })
      measure()
    }
    onResize()
    // Feeds and other asynchronously loaded content can grow after the menu opens.
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    window.addEventListener('resize', onResize)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', onResize)
    }
  }, [menuState.open])

  // Measured before it's painted, at its natural width; from then on it keeps that width. The window
  // may have resized while it was closed, so it waits for the viewport (and its screen cap) to catch up.
  useLayoutEffect(() => {
    const element = contextMenuRef.current
    if (!fit || !anchor || !element || fittedWidth !== undefined || viewport.width !== window.innerWidth) return
    setFitted({ anchor, width: Math.min(Math.ceil(element.getBoundingClientRect().width), componentWidth) })
  }, [fit, anchor, componentWidth, fittedWidth, viewport.width])

  const minBottom = menuBottomGap(viewportMargin)

  const clampToViewport = (num: number, axis: 'horizontal' | 'vertical') => {
    if (axis === 'horizontal') {
      const effectiveWidth = Math.min(fittedWidth ?? componentWidth, Math.max(0, viewport.width - 2 * viewportMargin))
      // Keep `viewportMargin` clear on both sides; never let the menu sit flush against an edge.
      return clamp(num, viewportMargin, viewport.width - effectiveWidth - viewportMargin)
    }
    return clamp(num, viewportMargin, viewport.height - componentHeight - minBottom)
  }

  const getValidatedPosition = (value: string, axis: 'horizontal' | 'vertical', dragOffset: number) => {
    const position = clampToViewport(parseFloat(value.replace(/px$/, '')), axis)
    // A dragged menu moves from where it opened on screen, not from its anchor, which can lie off
    // screen — offsetting the anchor would leave the first part of the drag eaten by the clamp.
    return `${dragOffset ? clampToViewport(position + dragOffset, axis) : position}px`
  }

  // A menu can also open upwards, its bottom edge just above its trigger: `top: 'auto'` with a
  // `bottom` (distance from the window's bottom edge). For triggers low on the screen, like the
  // account button at the foot of the sidebar: pushed up to fit below them, a menu slid over its
  // own trigger and put its last row (Sign Out) under the pointer.
  const opensUpwards = !inline && !fullScreen && menuState.top === 'auto' && menuState.bottom !== 'auto'
  const validatedBottom = opensUpwards
    ? `${Math.max(minBottom, Math.max(minBottom, parseFloat(menuState.bottom.replace(/px$/, '')) || 0) - offsetY)}px`
    : undefined

  // An inline menu's coordinates are relative to its anchor and needn't be pixels, so a drag adds to them.
  const offsetInline = (value: string, dragOffset: number) => (dragOffset ? `calc(${value} + ${dragOffset}px)` : value)
  const validatedTop = inline
    ? offsetInline(menuState.top, offsetY)
    : getValidatedPosition(menuState.top, 'vertical', offsetY)
  const validatedLeft = inline
    ? offsetInline(menuState.left, offsetX)
    : getValidatedPosition(menuState.left, 'horizontal', offsetX)

  const getMaxHeight = () => {
    const margin = 10
    // Opening upwards, the menu has the room above its trigger, and scrolls within it.
    if (validatedBottom) {
      return Math.max(0, viewport.height - parseFloat(validatedBottom) - Math.max(margin, viewportMargin))
    }
    // No artificial cap: the menu renders at its natural height (h-fit) and is only bounded to
    // the viewport, so content taller than the screen scrolls instead of overflowing. This is the
    // full viewport height rather than the space below the menu's top, because getValidatedPosition
    // shifts the menu upward when it would run off the bottom — so a viewport-height cap always fits.
    return Math.max(0, viewport.height - viewportMargin - Math.max(margin, viewportMargin))
  }
  const maxHeight = getMaxHeight()

  if (!menuState.open) {
    return null
  }

  const menuElement = (
    <div
      id={id}
      data-testid={id}
      data-nested-menu={nested ? 'true' : undefined}
      data-menu=""
      ref={contextMenuRef}
      className={menu({
        fit,
        pinned: fixed && !inline,
        fullScreen,
        drop: fullScreen ? 'none' : opensUpwards ? 'up' : 'down'
      })}
      style={
        fullScreen
          ? { zIndex: zIndex || 1000 }
          : {
              ...(validatedBottom ? { bottom: validatedBottom } : { top: validatedTop }),
              left: validatedLeft,
              maxWidth: `${inline ? componentWidth : Math.min(componentWidth, Math.max(0, viewport.width - 2 * viewportMargin))}px`,
              // w-full resolves against the parent; inline anchors have narrow parents, so pin the width.
              ...(inline ? { width: `${componentWidth}px` } : {}),
              ...(fit
                ? {
                    minWidth: `${Math.min(minWidth, Math.max(0, viewport.width - 2 * viewportMargin))}px`,
                    ...(fittedWidth ? { width: `${fittedWidth}px` } : {})
                  }
                : {}),
              zIndex: zIndex || 1000,
              // Always clamp to the viewport on both axes so any menu that would overflow becomes
              // scrollable
              maxHeight: `${maxHeight}px`
            }
      }
    >
      <MenuCloseContext.Provider value={handleMenuClose}>{children}</MenuCloseContext.Provider>
    </div>
  )

  return inline && !fullScreen ? menuElement : ReactDOM.createPortal(menuElement, rootComponent || appRootElement)
}

export default Menu
